import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Badge, Button } from 'react-bootstrap';
import { getAllBlogEntries, getAllAlbums, getImagesByAlbum } from "../../api/requests";


function AlbumThumbs({ albumId, images }) {
    // Zeige bis zu 4 Thumbs, letzter mit "+N" Overlay
    const maxThumbs = 5;
    if (!images || images.length === 0) return null;
    const thumbs = images.slice(0, maxThumbs);
    const extra = images.length - maxThumbs;
    return (
        <div style={{ display: "flex", gap: 4, marginTop: 8 }}>
            {thumbs.map((img, idx) => (
                <figure key={img.id} style={{ margin: 0, position: "relative" }}>
                    <img
                        src={img.image}
                        alt={img.title || ""}
                        loading="lazy"
                        decoding="async"
                        style={{
                            width: 48,
                            height: 48,
                            objectFit: "cover",
                            borderRadius: 6,
                            aspectRatio: "1/1",
                            background: "#eee"
                        }}
                    />
                    {extra > 0 && idx === maxThumbs - 1 && (
                        <span style={{
                            position: "absolute",
                            top: 0, left: 0, width: "100%", height: "100%",
                            background: "rgba(0,0,0,0.5)",
                            color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
                            fontWeight: 600, fontSize: 18, borderRadius: 6
                        }}>
                            +{extra}
                        </span>
                    )}
                </figure>
            ))}
        </div>
    );
}

function BlogReader() {
    const [blogEntries, setBlogEntries] = useState([]);
    const [albums, setAlbums] = useState([]);
    const [albumImages, setAlbumImages] = useState({});

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [entries, albumList] = await Promise.all([
                    getAllBlogEntries(),
                    getAllAlbums()
                ]);
                setBlogEntries(entries);
                setAlbums(albumList);

                // Lade für alle verknüpften Alben die öffentlichen Bilder
                const albumIds = entries.map(e => e.album).filter(Boolean);
                const imagesObj = {};
                await Promise.all(albumIds.map(async (aid) => {
                    if (!imagesObj[aid]) {
                        const imgs = await getImagesByAlbum(aid);
                        imagesObj[aid] = imgs || [];
                    }
                }));
                setAlbumImages(imagesObj);
            } catch (error) {
                console.error('Error fetching data:', error);
            }
        };
        fetchData();
    }, []);

    // Hilfsfunktion, um Albumdaten zu finden
    const getAlbum = (albumId) => albums.find(a => a.id === albumId);

    // Responsives Grid: 1 Spalte mobil, 2 ab 600px, 3 ab 900px
    return (
        <main style={{ background: "#F7F7F7", minHeight: "100vh", padding: "32px 0" }}>
            <Container>
                <Row xs={1} sm={2} lg={3} style={{ gap: 32 }}>
                    {blogEntries.length === 0 ? (
                        <Col>
                            <article>
                                <p>Keine Blogeinträge vorhanden.</p>
                            </article>
                        </Col>
                    ) : (
                        blogEntries.map(entry => {
                            const album = getAlbum(entry.album);
                            const images = albumImages[entry.album] || [];
                            const heroImg = album && images.length > 0 ? images[0].image : entry.image;
                            const albumPhotoCount = images.length;
                            return (
                                <Col key={entry.id} style={{ marginBottom: 32 }}>
                                    <article
                                        style={{
                                            background: "#fff",
                                            borderRadius: 16,
                                            boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
                                            overflow: "hidden",
                                            display: "flex",
                                            flexDirection: "column",
                                            minHeight: 420
                                        }}
                                        itemScope
                                        itemType="https://schema.org/BlogPosting"
                                    >
                                        <header>
                                            <figure style={{ margin: 0, position: "relative" }}>
                                                {heroImg && (
                                                    <img
                                                        src={heroImg}
                                                        alt={entry.title}
                                                        loading="lazy"
                                                        decoding="async"
                                                        style={{
                                                            width: "100%",
                                                            height: 220,
                                                            objectFit: "cover",
                                                            aspectRatio: "16/7",
                                                            background: "#eee"
                                                        }}
                                                    />
                                                )}
                                                {album && (
                                                    <Badge
                                                        bg="primary"
                                                        style={{
                                                            position: "absolute",
                                                            top: 16,
                                                            left: 16,
                                                            fontSize: 15,
                                                            padding: "6px 14px",
                                                            borderRadius: 20,
                                                            opacity: 0.93,
                                                            letterSpacing: 0.5
                                                        }}
                                                    >
                                                        Album · {albumPhotoCount} Fotos
                                                    </Badge>
                                                )}
                                            </figure>
                                        </header>
                                        <section style={{ padding: 24, flex: 1, display: "flex", flexDirection: "column" }}>
                                            <time
                                                dateTime={entry.date}
                                                style={{
                                                    color: "#888",
                                                    fontSize: 15,
                                                    marginBottom: 8,
                                                    display: "block"
                                                }}
                                            >
                                                {entry.date && new Date(entry.date).toLocaleDateString()}
                                            </time>
                                            <h2 style={{
                                                fontFamily: "'Merriweather', serif",
                                                fontSize: 26,
                                                margin: "0 0 12px 0",
                                                color: "#222"
                                            }}>{entry.title}</h2>
                                            <p style={{
                                                fontFamily: "Inter, Manrope, Arial, sans-serif",
                                                fontSize: 17,
                                                color: "#444",
                                                marginBottom: 16,
                                                minHeight: 48
                                            }}>
                                                <span className="plain-text">
                                                    {entry.text.length > 200 ? entry.text.substring(0, 200) + "..." : entry.text}
                                                </span>
                                            </p>
                                            {album && (
                                                <AlbumThumbs albumId={album.id} images={images} />
                                            )}
                                            <div style={{ marginTop: "auto", display: "flex", gap: 16 }}>
                                                <Button variant="link" style={{ padding: 0, fontSize: 16 }} href={`/tsc-blog/${entry.id}`}>
                                                    Ganzen Beitrag lesen
                                                </Button>
                                                {album && (
                                                    <Button variant="link" style={{ padding: 0, fontSize: 16 }} href={`/album/${album.id}`}>
                                                        Album öffnen
                                                    </Button>
                                                )}
                                                <Button variant="link" style={{ padding: 0, fontSize: 16 }} onClick={() => navigator.share && navigator.share({ title: entry.title, url: window.location.href })}>
                                                    Teilen
                                                </Button>
                                            </div>
                                        </section>
                                    </article>
                                </Col>
                            );
                        })
                    )}
                </Row>
            </Container>
        </main>
    );
}

export default BlogReader;
